// packages/web/src/lib/asks.ts — S11: how an ask READS, in one place.
//
// Two surfaces show asks (Today's "Needs you" and the asks screen itself)
// and they must say the same words about the same row, so the words live
// here rather than twice. Nothing in this file fetches anything.
//
// THE FOLD is the important part. wf02 §02 already told us where an ask
// belongs: "Needs you" is "attention items in severity order… Each is a
// real thing with an owner: a receipt waiting on authorization, a margin
// outside Alan's band, a follow-up due." An ask routed to you IS a receipt
// waiting on authorization — so it joins the list that already exists,
// shaped into the read model that list and lib/attentionRoute's one doorway
// table already speak. No second attention concept, and nothing added
// server-side: the server's attention producer knows nothing about asks and
// this chunk does not teach it (that would be a Core chunk).
import type { Ask, AttentionItem } from './api';

/** WHAT IS BEING ASKED, in words. An action id is
 * `<pack>.<subject>.<verb>` — the pack prefix is noise to a person reading
 * their own shop's screen, and the rest is already the sentence:
 * 'rexx.pay_basis.change' -> "Pay basis change". Falls back to the ask's
 * own title, which is either the requester's words or Core's derived line. */
export function askActionLabel(ask: Pick<Ask, 'actionId' | 'title'>): string {
  const dot = ask.actionId.indexOf('.');
  const withoutPack = dot >= 0 ? ask.actionId.slice(dot + 1) : ask.actionId;
  const words = withoutPack.replace(/[._]/g, ' ').trim();
  if (!words) return ask.title;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** THE OPENED ASK'S HEADLINE. Core derives an unworded ask's title as
 *  "Approval needed: <actionId>", and that raw id is what an opened ask
 *  showed ("Approval needed: actor_assist.source.create", 2026-09-29). Rows
 *  filed before Core worded it still carry the raw form, so it is re-said
 *  here in the row label's own words; a requester's own title is left as
 *  they wrote it. */
export function askTitle(ask: Pick<Ask, 'actionId' | 'title'>): string {
  if (ask.title.trim() === `Approval needed: ${ask.actionId}`) {
    return `Approval needed: ${askActionLabel(ask).toLowerCase()}`;
  }
  return ask.title;
}

/** G45: is this a DOOR ask — a single call through the door registry
 * (deliver a document, send an agreement, un-happen a cascade) rather than
 * a records-engine remainder. Missing `kind` (every ask filed before G45)
 * reads as 'cascade', the server's own default. */
export function isDoorAsk(ask: Pick<Ask, 'kind'>): boolean {
  return ask.kind === 'door';
}

/** THE SENTENCE a door ask shows — bullet 5's own words: "renders a door
 * ask as its sentence + the reverse sentence". Baked into ONE string by
 * whoever registered the door (cascade/coreDoors.ts's `describe`), because
 * the REAL reverse cannot be known until approval actually performs it —
 * see cascade/doors.ts's header. Null for a cascade ask, or a door ask
 * whose `doorDescribe` is somehow absent (never true for a G45-filed row;
 * defensive for anything stranger). */
export function doorAskSentence(ask: Pick<Ask, 'kind' | 'doorDescribe'>): string | null {
  if (!isDoorAsk(ask)) return null;
  return ask.doorDescribe?.trim() || null;
}

export interface AskOutcome {
  /** One word for the row's right edge. */
  label: string;
  /** 'waiting' | 'approved' | 'declined' — the only three a person cares
   * about; the screen maps these to colour. */
  tone: 'waiting' | 'approved' | 'declined';
  /** The decline's own words, when there are any to show. Core writes
   * 'expired' and 'stale_manifest' itself; both are rendered as sentences
   * rather than left as machine words, because the requester is the reader. */
  detail: string | null;
}

const MACHINE_REASONS: Record<string, string> = {
  expired: 'Nobody answered in time, so it closed itself.',
  // Product-neutral (GAP 57 readiness): "shop" was Rexx's word, and
  // CallToWrap renders this screen too.
  stale_manifest: 'The rules changed after this was asked, so it could not be carried out.',
};

/** HOW IT ENDED, for the person who asked. A decline whose reason never
 * reaches them is the invisible outcome this product exists to avoid, so
 * the reason is part of the outcome here, not a detail a caller may drop. */
export function askOutcome(ask: Pick<Ask, 'status' | 'decisionReason'>): AskOutcome {
  if (ask.status === 'open') return { label: 'Waiting', tone: 'waiting', detail: null };
  if (ask.status === 'done') return { label: 'Approved', tone: 'approved', detail: null };
  const reason = ask.decisionReason?.trim() ?? '';
  return {
    label: 'Declined',
    tone: 'declined',
    detail: reason ? MACHINE_REASONS[reason] ?? reason : null,
  };
}

/** An ask routed to me, shaped as the attention row Today already renders
 * and already knows how to open (objectType 'ask' is resolved by
 * lib/attentionRoute's Rexx branch). Severity is 'warning', not 'critical':
 * a person is waiting on you, which is not the same as a fire, and an ask
 * must not outrank a genuine critical in a list whose whole promise is
 * severity order. */
export function askAsAttentionItem(ask: Ask): AttentionItem {
  return {
    id: `ask:${ask.id}`,
    userId: null,
    itemType: 'ask',
    objectType: 'ask',
    objectId: ask.id,
    severity: 'warning',
    reason: askActionLabel(ask),
    sourceRef: null,
    dedupKey: `ask:${ask.id}`,
    createdAt: ask.createdAt,
    resolvedAt: null,
    lastRaisedAt: ask.createdAt,
  };
}
